import type { AuthUser } from '@/lib/auth/constants';
import { create } from 'zustand';

type LoginForm = {
  email: string;
  password: string;
};

type SignupForm = {
  name: string;
  email: string;
  password: string;
};

type LoginCredentials = {
  email: string;
  password: string;
};

type AuthState = {
  loginForm: LoginForm;
  signupForm: SignupForm;
  statusMessage?: string;
  isLoading: boolean;
  isInitializing: boolean;
  user: AuthUser | null;
  setLoginField: <K extends keyof LoginForm>(
    field: K,
    value: LoginForm[K],
  ) => void;
  setSignupField: <K extends keyof SignupForm>(
    field: K,
    value: SignupForm[K],
  ) => void;
  resetLoginForm: () => void;
  resetSignupForm: () => void;
  setStatusMessage: (message?: string) => void;
  clearStatusMessage: () => void;
  signup: () => Promise<{ success: boolean; error?: string }>;
  login: (
    credentials?: LoginCredentials,
  ) => Promise<{ success: boolean; error?: string }>;
  logout: () => Promise<void>;
  loginWithMicrosoft: (callbackUrl?: string) => void;
  fetchSession: () => Promise<void>;
  setUser: (user: AuthUser | null) => void;
  setLoading: (loading: boolean) => void;
  setInitializing: (initializing: boolean) => void;
};

const initialLoginForm: LoginForm = {
  email: '',
  password: '',
};

const initialSignupForm: SignupForm = {
  name: '',
  email: '',
  password: '',
};

function isIntranetClientContext(): boolean {
  if (typeof window === 'undefined') return false;
  return (
    window.location.pathname.startsWith('/intranet') ||
    window.location.hostname.startsWith('intranet.')
  );
}

const authFetch = (input: RequestInfo, init?: RequestInit) => {
  const headers = new Headers(
    init?.headers instanceof Headers
      ? init.headers
      : new Headers(init?.headers as HeadersInit),
  );
  if (isIntranetClientContext()) {
    headers.set('X-Intranet-Context', 'true');
  }
  return fetch(input, { ...init, credentials: 'include', headers });
};

export const useAuthStore = create<AuthState>((set, get) => ({
  loginForm: initialLoginForm,
  signupForm: initialSignupForm,
  statusMessage: undefined,
  isLoading: false,
  isInitializing: true,
  user: null,

  setLoginField: (field, value) =>
    set((state) => ({
      loginForm: {
        ...state.loginForm,
        [field]: value,
      },
    })),

  setSignupField: (field, value) =>
    set((state) => ({
      signupForm: {
        ...state.signupForm,
        [field]: value,
      },
    })),

  resetLoginForm: () => set({ loginForm: initialLoginForm }),

  resetSignupForm: () => set({ signupForm: initialSignupForm }),

  setStatusMessage: (message) => set({ statusMessage: message }),

  clearStatusMessage: () => set({ statusMessage: undefined }),

  setUser: (user) => set({ user }),

  setLoading: (loading) => set({ isLoading: loading }),

  setInitializing: (initializing) => set({ isInitializing: initializing }),

  fetchSession: async () => {
    set({ isInitializing: true });
    try {
      const response = await authFetch('/api/auth/me');
      if (response.ok) {
        const data = await response.json();
        set({ user: data.user });
      } else {
        set({ user: null });
      }
    } catch {
      set({ user: null });
    } finally {
      set({ isInitializing: false });
    }
  },

  logout: async () => {
    set({ isLoading: true });
    try {
      await authFetch('/api/auth/logout', { method: 'POST' });
    } catch {
      // Clear local state even if the request fails
    } finally {
      set({
        user: null,
        isLoading: false,
        statusMessage: undefined,
        loginForm: initialLoginForm,
        signupForm: initialSignupForm,
      });
    }
  },

  loginWithMicrosoft: (callbackUrl = '/') => {
    const params = new URLSearchParams({
      callbackUrl,
    });
    window.location.href = `/api/auth/microsoft/login?${params.toString()}`;
  },

  signup: async () => {
    const { signupForm } = get();

    if (!signupForm.name || !signupForm.email || !signupForm.password) {
      const error = 'Tutti i campi sono obbligatori';
      set({ statusMessage: error });
      return { success: false, error };
    }

    set({ isLoading: true, statusMessage: undefined });

    try {
      const response = await authFetch('/api/auth/signup', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: signupForm.name,
          email: signupForm.email,
          password: signupForm.password,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        const error = data.error || 'Errore durante la registrazione';
        set({
          statusMessage: error,
          isLoading: false,
        });
        return { success: false, error };
      }

      set({
        user: null,
        isLoading: false,
        statusMessage:
          data.message ||
          'Controlla la tua email per confermare l\'account.',
      });

      set({ signupForm: initialSignupForm });

      return { success: true };
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'Errore di connessione. Riprova più tardi.';
      set({
        statusMessage: errorMessage,
        isLoading: false,
      });
      return { success: false, error: errorMessage };
    }
  },

  login: async (credentials) => {
    const email = credentials?.email ?? get().loginForm.email;
    const password = credentials?.password ?? get().loginForm.password;

    if (!email || !password) {
      const error = 'Email e password sono obbligatori';
      set({ statusMessage: error });
      return { success: false, error };
    }

    set({ isLoading: true, statusMessage: undefined });

    try {
      const response = await authFetch('/api/auth/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          identifier: email,
          password,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        const error = data.error || 'Errore durante il login';
        set({
          statusMessage: error,
          isLoading: false,
        });
        return { success: false, error };
      }

      set({
        user: data.user,
        isLoading: false,
        statusMessage: 'Accesso effettuato con successo!',
      });

      set({ loginForm: initialLoginForm });

      return { success: true };
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'Errore di connessione. Riprova più tardi.';
      set({
        statusMessage: errorMessage,
        isLoading: false,
      });
      return { success: false, error: errorMessage };
    }
  },
}));

export const useIsAuthenticated = () =>
  useAuthStore((state) => state.user !== null);
